import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { startOfDay, addYears } from "date-fns";
import { Box } from "@mui/material";
import truncate from "lodash/truncate";
import { useAccountsId, useInvestmentsId } from "@rentablo/api/apiv1";
import { CardPageLayout } from "@rentablo/components";
import { useRangeSelectControl } from "@hooks";
import { useMandatorVisibility } from "@helpers";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import { ChartControlsProvider } from "@components/Chart/ChartControls";
import { RecommendedItem } from "./hooks/useRecommededHistoricQuotes";
import { InvestmentIsinBanner } from "./components/InvestmentIsinBanner";
import { InvestmentPageChart } from "./components/InvestmentPageChart";
import { InvestmentPageHeading } from "./components/InvestmentPageHeading";
import { InvestmentPageTabs } from "./components/InvestmentPageTabs";
import { InvestmentRecommender } from "./components/InvestmentRecommender";
import { getDisplayedQuoteValue } from "./components/InvestmentTable/getDisplayedQuoteValue";
import { RecommendedItems } from "./types";

const availableIntervalTypes: IntervalType[] = [
  "oneDay",
  "oneWeek",
  "oneMonth",
  "YTD",
  "oneYear",
  "threeYears",
  "fiveYears",
  "sinceBeginning",
  "custom",
];
const availableCompatIntervalTypes: IntervalType[] = [
  "oneDay",
  "oneWeek",
  "oneMonth",
  "oneYear",
  "fiveYears",
  "custom",
];

export interface Props {
  investmentId: number;
  depotId?: number;
}

export const InvestmentPage = ({ investmentId, depotId }: Props) => {
  const { t } = useTranslation();
  const { data: account } = useAccountsId(
    { id: depotId! },
    { enabled: !!depotId }
  );
  const isComparisonSelectorVisible = useMandatorVisibility({
    hideFor: ["kpmg-germany"],
  });
  const {
    data: investment,
    isFetching: isInvestmentLoading,
    isPending: isInvestmentPending,
  } = useInvestmentsId(
    { id: investmentId! },
    { enabled: investmentId !== undefined }
  );
  const { selectedCurrencyView, getDisplayCurrency } = useCurrencyDisplay();
  const isOriginal = selectedCurrencyView === "original";
  const displayCurrency = getDisplayCurrency(investment?.currency);

  const protocolRangeProps = useRangeSelectControl({
    firstDate: new Date(),
    defaultIntervalType: "sinceBeginning",
  });

  const [recommendedItems, setRecommendedItems] = useState<RecommendedItems>({
    recommender1: undefined,
    recommender2: undefined,
    recommender3: undefined,
  });

  const investmentChartFirstDate = startOfDay(addYears(new Date(), -1));
  const quoteValue = getDisplayedQuoteValue(investment?.quote, isOriginal);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      to: "/",
      text: t("breadcrumbs.home"),
    },
    depotId
      ? {
          to: "/depots-accounts",
          text: t("breadcrumbs.depotsAccounts"),
        }
      : {
          to: "/investments",
          text: t("breadcrumbs.investments"),
        },
    ...(account
      ? [
          {
            to: `/depots/${depotId}`,
            text: truncate(account?.name),
          },
        ]
      : []),
    {
      text:
        (investment?.hasValidIsin
          ? investment?.isin
          : truncate(investment?.name)) ?? "",
    },
  ];

  const handleRecommendationSelect = (item: RecommendedItem, index: number) => {
    const key = `recommender${index + 1}`;
    setRecommendedItems((prev) => ({
      ...prev,
      [key]: recommendedItems[key as keyof RecommendedItems] ? undefined : item,
    }));
  };

  useEffect(() => {
    const currentEl = document.getElementById("content");
    if (!currentEl) return;
    const handleScroll = () => {
      currentEl.scrollTo(0, 0);
    };
    window.addEventListener("popstate", handleScroll, true);
    return () => {
      window.removeEventListener("popstate", handleScroll);
    };
  }, []);

  return (
    <>
      <CardPageLayout
        id="content"
        data-testid="investment-page"
        breadcrumbs={breadcrumbs}
        banner={
          <InvestmentIsinBanner
            investmentId={investmentId}
            investment={investment}
          />
        }
        cardHeading={
          <InvestmentPageHeading
            investmentId={investmentId}
            investment={investment}
          />
        }
        cardChildren={
          <ChartControlsProvider
            rangeId="investment"
            rangeFirstDate={investmentChartFirstDate}
            availableDateRanges={availableIntervalTypes}
            availableCompactDateRanges={availableCompatIntervalTypes}
            controls={{ showCompare: isComparisonSelectorVisible }}
          >
            <Box>
              <InvestmentPageChart
                investmentId={investmentId}
                investment={investment}
                isInvestmentLoading={isInvestmentLoading}
                isInvestmentPending={isInvestmentPending}
                recommendedItems={recommendedItems}
              />
            </Box>
          </ChartControlsProvider>
        }
      >
        <InvestmentPageTabs
          investmentId={investmentId}
          investment={investment}
          isInvestmentLoading={isInvestmentLoading}
          quoteValue={quoteValue}
          displayCurrency={displayCurrency}
          isOriginal={isOriginal}
          protocolRangeProps={protocolRangeProps}
        />
      </CardPageLayout>
      <InvestmentRecommender
        investmentId={investmentId}
        isin={investment?.isin}
        recommendedItems={recommendedItems}
        onRecommendationSelect={handleRecommendationSelect}
      />
    </>
  );
};
